import React, {useEffect, useRef, useState} from 'react';
import {Rating} from "@mui/material";
import {useDispatch, useSelector} from "react-redux";
import toast from "react-hot-toast";
import {useNavigate} from "react-router-dom";
import {useQuery} from "@tanstack/react-query";

import styles from './FeedbackPageCustomer.module.scss';
import "../../utils/star.scss";
import {feedbackCustomerActions} from "../../store/slices/feedbackCustomerSlice";
import AddPhotoFeedbackPage from "../../components/AddPhotoFeedbackPage/AddPhotoFeedbackPage";
import MainButtonWithBackground from "../../components/UI/MainButtonWithBackground/MainButtonWithBackground";
import {feedbackService} from "../../services/feedback.service";
import {MAIN_ROUTES} from "../../routing/main_routes";
import MainButtonTransparentBackground
    from "../../components/UI/MainButtonTransparentBackground/MainButtonTransparentBackground";
import {qrCodeActions} from "../../store/slices/qrCodeSlice";
import {qrCodeService} from "../../services/qrCode.service";
import IsCustomer from "../../HOC/IsCustomer";
import {feedbackCleanerActions} from "../../store/slices/feedbackCleanerSlice";
import {urlsToFiles} from "../../utils/urlsToFiles";

const FeedbackPageCustomer = ({handleClose, order}) => {
    const [generalService, setGeneralService] = useState(0);
    const [service1, setService1] = useState(0);

    const dispatch = useDispatch();
    const {
        feedback_text,
        private_feedback_text,
        photos_completed_order,
    } = useSelector(state => state.feedbackCustomer);
    const {session_id, photos, qr_code} = useSelector(state => state.qrCode);

    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            handleClose();
            dispatch(feedbackCustomerActions.clearAllData());
            setGeneralService(null);
            setService1(null);
        }
    }, [])


    const turnReceivedImgUrlsToFiles = (photoUrls) => urlsToFiles(photoUrls);

    const generateQrCode = async () => {
        try {
            await dispatch(qrCodeActions.generate());
        } catch (error) {
            console.error(error);
        }
    }

    const visibilityRef = useRef(null);

    useEffect(() => {
        visibilityRef.current = document.visibilityState;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                handleClose();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        const timer = setTimeout(() => {
            handleClose();
        }, 59 * 60 * 1000);

        return () => {
            handleClose();
            dispatch(feedbackCustomerActions.clearAllData());
            document.removeEventListener('visibilitychange', handleVisibilityChange);
            clearTimeout(timer);
        }
    }, []);

    const {data} = useQuery({
        queryKey: ['get-photos-qr-code'],
        queryFn: () => {
            if (session_id) {
                return qrCodeService.getPhotos(session_id);
            }
        },
        throwOnError: async (error) => {
            if (error?.response?.status === 404) {
                dispatch(qrCodeActions.clearAllData());
                await generateQrCode();
                alert('Cесія завантаження фото за допомогою QR коду закінчилась! Спробуйте ще раз!');
            }
        },
        refetchInterval: photos?.length === 0 && 3000,
        refetchIntervalInBackground: true
    });

    useEffect(() => {
        dispatch(qrCodeActions.setPhotos(data?.photos));
    }, [data])

    useEffect(() => {
        return () => {
            dispatch(qrCodeActions.clearAllData());
        }
    }, []);


    const handleCreateFormData = (photos, feedback, qrCodePhotos) => {
        let formData = new FormData();

        if (photos?.length > 0) {
            for (const photo of photos) {
                formData.append('photos_data', photo);
            }
        }

        if (qrCodePhotos?.length > 0) {
            for (const photo of qrCodePhotos) {
                formData.append('photos_data', photo);
            }
        }

        formData.append('feedback', JSON.stringify(feedback));

        return formData;
    };

    const handleCreateFeedback = async () => {
        try {
            const feedback = {
                feedback_text,
                order: order?.id,
                private_feedback_text,
                service_feedback: [{
                    assessment: service1,
                    service: generalService,
                }]
            }

            const files = await turnReceivedImgUrlsToFiles(photos);
            const formData = handleCreateFormData(photos_completed_order, feedback, files)

            await feedbackService.createForCleaner(formData);
            handleClose();
            toast.success(`Відгук успішно надіслано`, {id: "feedback-customer"});
            setTimeout(() => {
                navigate(MAIN_ROUTES.MAIN);
            }, 2000)
        } catch (error) {
            toast.error(`Відгук не вдалось надіслати. Спробуйте, будь ласка, ще раз!${JSON.stringify(error?.response?.data)}`, {id: "feedback-customer"});
        } finally {
            dispatch(feedbackCustomerActions.clearAllData());
            setGeneralService(0);
            setService1(0);
            handleClose();
        }
    }

    const {id, name, surname, cleaningType} = order;

    return (
        <div className={styles.feedbackPageCustomer_wrapper}>
            <div className={styles.feedbackPageCustomer}>
                <h3>Залишити відгук</h3>

                <div className={styles.feedbackPageCustomer_rating}>
                    <div className={styles.feedbackPageCustomer_rating_feedback}>
                        <p>Залишіть відгук клінеру {name} {surname}</p>
                        <textarea
                            value={feedback_text}
                            onChange={(e) => dispatch(feedbackCustomerActions.setFeedbackText(e.target.value))}
                            placeholder={'Ваш відгук'}
                            rows="4"
                        ></textarea>
                    </div>

                    <div className={styles.feedbackPageCustomer_rating_star}>
                        <p>Якість обслуговування</p>
                        <Rating
                            style={{alignSelf: 'flex-start'}}
                            size="large"
                            name="simple-controlled"
                            value={generalService}
                            onChange={(event, newValue) => setGeneralService(newValue)}
                        />
                    </div>

                    <div className={styles.feedbackPageCustomer_rating_star}>
                        <p>Якість наданої послуги ({cleaningType})</p>
                        <Rating
                            style={{alignSelf: 'flex-start'}}
                            size="large"
                            name="simple-controlled"
                            value={service1}
                            onChange={(event, newValue) => setService1(newValue)}
                        />
                    </div>

                    <div className={styles.feedbackPageCustomer_rating_feedback}>
                        <p>Якщо були зауваження в роботі {name}, залишіть приватний відгук</p>
                        <textarea
                            value={private_feedback_text}
                            onChange={(e) => dispatch(feedbackCustomerActions.setPrivateFeedbackText(e.target.value))}
                            placeholder={'Ваш відгук'}
                            rows="4"
                        ></textarea>
                    </div>
                </div>

                <AddPhotoFeedbackPage type={'customer'}/>

                <div className={styles.feedbackPageCustomer_buttons}>
                    <MainButtonTransparentBackground
                        onClick={handleClose}
                        text={'Скасувати'}
                        condition={false}
                        type={'button'}
                        width={'auto'}
                    />

                    <MainButtonWithBackground
                        onClick={handleCreateFeedback}
                        text={'Підтвердити'}
                        condition={!feedback_text || !service1 || !generalService || !private_feedback_text}
                        type={'button'}
                        width={'auto'}
                    />
                </div>
            </div>
        </div>
    );
};

export default IsCustomer(FeedbackPageCustomer);
